import React, { useEffect, useState } from 'react';
import { getAccessToken } from '../../services/authApi';
import { fieldReader } from '../../services/loanApi';
import LoadingOverlay from '../shared/LoadingOverlay';
import Toast from '../shared/Toast';
import { runLicensingGateForPage } from '../../utils/licenseValidation';

const LicensingPage = () => {
    const pageName = 'Licensing';
    const [loading, setLoading] = useState(true);
    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastAnimation, setToastAnimation] = useState('animate-fade-in-right');

    const [licenseStatus, setLicenseStatus] = useState({
        blocked: false,
        companyRequired: false,
        loRequired: false,
        companyHas: false,
        loHas: false,
        stateCode: '',
        city: '',
        stateName: '',
        reason: '',
        message: '',
        shortMessage: '',
        brokerSelection: ''
    });

    const showToastMsg = (msg) => {
        setToastMessage(msg);
        setToastVisible(true);
    };

    useEffect(() => {
        let hideTimer, vanishTimer;
        if (toastVisible) {
            setToastAnimation('animate-fade-in-right');
            hideTimer = setTimeout(() => {
                setToastAnimation('animate-fade-out-right');
                vanishTimer = setTimeout(() => {
                    setToastVisible(false);
                }, 500);
            }, 4000);
        }
        return () => {
            clearTimeout(hideTimer);
            clearTimeout(vanishTimer);
        };
    }, [toastVisible]);

    useEffect(() => {
        const init = async () => {
            try {
                window.elli?.script?.guest?.create?.();

                const loanObject = await window.elli.script.getObject('loan');
                const loan = await loanObject.all();

                const authObject = await window.elli.script.getObject('auth');
                const authCode = await authObject.createAuthCode();
                const accessToken = await getAccessToken(authCode);

                // Read fields
                // 14 = Subject Property State
                // 12 = Subject Property City
                // TPO.X15 = TPO org id
                // TPO.X62 = TPO user id
                // CX.LOAN.TYPE = Consumer/Business
                const values = await fieldReader(
                    accessToken,
                    ['14', 'TPO.X15', 'TPO.X62', '12', 'CX.LOAN.TYPE', 'CX.LEGAL.ENTITY.TYPE'],
                    loan.id
                );

                const stateCode = (values['14'] || '').trim();
                const tpoOrgId = (values['TPO.X15'] || '').trim();
                const tpoUserId = (values['TPO.X62'] || '').trim();
                const city = (values['12'] || '').trim();
                const loanType = (values['CX.LOAN.TYPE'] || '').trim();
                const legalEntityType = (values['CX.LEGAL.ENTITY.TYPE'] || '').trim();

                const status = await runLicensingGateForPage(loanType, stateCode, tpoOrgId, tpoUserId, city, legalEntityType);
                setLicenseStatus({
                    ...status,
                    city
                });

            } catch (err) {
                console.error('Licensing page init error', err);
                showToastMsg('Failed to load licensing details.');
            } finally {
                setLoading(false);
            }
        };

        init();
    }, []);

    const noIssues =
        !licenseStatus.blocked &&
        (!licenseStatus.companyRequired || licenseStatus.companyHas) &&
        (!licenseStatus.loRequired || licenseStatus.loHas);


    const msgLower = (licenseStatus.message || '').toLowerCase();

    const isRestrictionMessage =
        msgLower.includes('not eligible for')

    const sectionHeaderText = isRestrictionMessage
        ? 'Location Restriction'
        : 'Licensing';
    const pageTitle = isRestrictionMessage
        ? 'Location Restriction'
        : pageName;

    return (
        <>
            <LoadingOverlay loading={loading} text="Loading..." />
            <Toast
                visible={toastVisible}
                type="warning"
                message={toastMessage}
                onClose={() => setToastVisible(false)}
                animationClass={toastAnimation}
            />

            <div className="space-y-10 p-25px">
                <div className="rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                    <h2 className="page-title">{pageTitle}</h2>

                    {!loading && (
                        <div className="border border-blue-200 rounded bg-gray-50">
                            <div className="section-header">{sectionHeaderText}</div>

                            <div className="p-4 space-y-4">
                                {noIssues ? (
                                    <div className="flex items-start gap-3">
                                        <div className="mt-1 inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded bg-green-100 text-green-700">
                                            No Issues
                                        </div>
                                        <div>
                                            <div className="text-base font-semibold text-green-700">
                                                All required licenses are valid. No licensing issues detected.
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex items-start gap-3">
                                            <div className="mt-1 inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded bg-red-100 text-red-700">
                                                {licenseStatus.blocked ? 'Blocking' : 'Attention'}
                                            </div>

                                            <div>
                                                <div className="text-base font-semibold">
                                                    {licenseStatus.blocked
                                                        ? (isRestrictionMessage
                                                            ? 'Location restriction'
                                                            : (licenseStatus.message?.toLowerCase().includes('licensing restrictions')
                                                                ? 'Licensing restrictions detected'
                                                                : 'Licensing verification required'))
                                                        : 'License Verification Summary'}
                                                    {isRestrictionMessage && licenseStatus.city
                                                        ? ` for ${licenseStatus.city}, ${licenseStatus.stateCode}`
                                                        : licenseStatus.stateCode
                                                            ? ` for ${licenseStatus.stateCode} state`
                                                            : ''}
                                                </div>

                                                <p
                                                    className="text-base text-red-700 mt-1 font-medium"
                                                    dangerouslySetInnerHTML={{
                                                        __html: licenseStatus.message || licenseStatus.reason || ''
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {!isRestrictionMessage && (
                                            <>
                                                <div className="grid md:grid-cols-2 gap-4">
                                                    <div className="border rounded p-3 bg-white">
                                                        <div className="text-sm font-medium mb-1">Company / Broker License</div>
                                                        <div className="text-sm">
                                                            {licenseStatus.companyRequired ? (
                                                                licenseStatus.companyHas ? (
                                                                    <span className="inline-flex items-center text-green-700">
                                                                        <span className="w-2 h-2 rounded-full inline-block mr-2 bg-green-600" /> Found
                                                                    </span>
                                                                ) : (
                                                                    <span className="inline-flex items-center text-red-700">
                                                                        <span className="w-2 h-2 rounded-full inline-block mr-2 bg-red-600" /> Missing
                                                                    </span>
                                                                )
                                                            ) : (
                                                                <span className="inline-flex items-center text-gray-600">
                                                                    <span className="w-2 h-2 rounded-full inline-block mr-2 bg-gray-400" /> Not Required
                                                                </span>
                                                            )}
                                                        </div>

                                                    </div>

                                                    <div className="border rounded p-3 bg-white">
                                                        <div className="text-sm font-medium mb-1">Loan Originator License</div>
                                                        <div className="text-sm">
                                                            {licenseStatus.loRequired ? (
                                                                licenseStatus.loHas ? (
                                                                    <span className="inline-flex items-center text-green-700">
                                                                        <span className="w-2 h-2 rounded-full inline-block mr-2 bg-green-600" /> Found
                                                                    </span>
                                                                ) : (
                                                                    <span className="inline-flex items-center text-red-700">
                                                                        <span className="w-2 h-2 rounded-full inline-block mr-2 bg-red-600" /> Missing
                                                                    </span>
                                                                )
                                                            ) : (
                                                                <span className="inline-flex items-center text-gray-600">
                                                                    <span className="w-2 h-2 rounded-full inline-block mr-2 bg-gray-400" /> Not Required
                                                                </span>
                                                            )}
                                                        </div>

                                                    </div>
                                                </div>

                                                {/* Optional: show broker selection if you want (since evaluateLicensing returns it) */}
                                                {licenseStatus.brokerSelection ? (
                                                    <div className="border rounded p-3 bg-white">
                                                        <div className="text-sm font-medium mb-1">Broker Selection</div>
                                                        <div className="text-sm">{licenseStatus.brokerSelection}</div>
                                                    </div>
                                                ) : null}
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default LicensingPage;
